/*
 * eu-seller/compose.js — from a product card to what is printed and pasted.
 *
 * Three label kinds (where the law lets each piece go):
 *   product — on the item: identifier (GPSR 9(5)), manufacturer (9(6)), the
 *             EU operator (16(3)), marks (CE, WEEE, battery, 0–3, CLP),
 *             fibre composition, toy / CLP / cosmetics wording;
 *   pack    — on the packaging: identifier and manufacturer again (PPWR
 *             15(5)–(6) from 12.08.2026), warnings that decide the purchase,
 *             the French sorting block (Triman), Italian material codes,
 *             Spanish containers;
 *   insert  — a sheet in the parcel: the EU operator (GPSR 16(3) names «the
 *             parcel»), manufacturer, identifier, warnings in every language,
 *             the complaints channel (9(11)).
 * And the texts for marketplace fields and the website's legal block.
 *
 * Every multilingual sentence comes from the official texts file
 * (public/data/eu-seller/texts-<lang>.json, built from EUR-Lex by
 * scripts/build-eu-seller-texts.mjs). What the law does not word — the
 * product name, addresses, the specific hazard of a toy — is the seller's
 * own text and is printed as typed.
 *
 * LAYOUT. A label is a vertical stack of blocks. `stack()` measures each
 * block with the SAME measurer label-maker's layout uses (injected), shrinks
 * the type together down to the floor the x-height rule allows, and answers
 * with label-maker elements in fractions of the label plus our symbol boxes
 * in millimetres — or with an honest «does not fit» and by how much.
 *
 * Pure module: no DOM. Tested by tests/js/eu-seller-compose.test.mjs.
 */
(function (root, factory) {
    'use strict';
    var api = factory(root && root.EuRules, root && root.EuSymbols, root && root.LabelLayout);
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.EuCompose = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function (RulesIn, SymbolsIn, LayoutIn) {
    'use strict';

    var deps = { R: RulesIn, S: SymbolsIn, L: LayoutIn };
    function use(d) { for (var k in d) if (d[k]) deps[k] = d[k]; }

    function str(v) { return v == null ? '' : String(v).trim(); }
    function cap(s) { s = str(s); return s ? s.charAt(0).toLocaleUpperCase() + s.slice(1) : s; }
    function endDot(s) { s = str(s); return /[.!?]$/.test(s) ? s : s + '.'; }
    /* Official phrases that leave a slot for the date with «…» (da, es, et, nl, pt). */
    function noSlot(s) { return str(s).replace(/\s*…\s*$/, ''); }

    /* ── captions: the official GPSR terms where they are nominative ─────── */
    function term(texts, lang, key) {
        var T = texts[lang] && texts[lang].terms;
        if (!T || T._inflected || !T[key]) T = texts.en && texts.en.terms;
        return T && T[key] ? cap(T[key]) : key;
    }
    function captionLang(texts, lang) {
        var T = texts[lang] && texts[lang].terms;
        return T && !T._inflected ? lang : 'en';
    }

    /* ── identification, manufacturer, EU operator ───────────────────────── */
    function idLine(card) {
        var parts = [];
        if (str(card.type)) parts.push(str(card.type));
        if (str(card.batch)) parts.push('LOT ' + str(card.batch));
        if (str(card.serial)) parts.push('S/N ' + str(card.serial));
        return parts.join(' · ');
    }
    function party(p) {
        p = p || {};
        return [str(p.name), str(p.address).replace(/\s*\n\s*/g, ', '), str(p.email), str(p.phone)].filter(Boolean).join(', ');
    }
    function mfrLine(card, texts, lang) {
        var body = party(card.mfr);
        return body ? term(texts, lang, 'manufacturer') + ': ' + body : '';
    }
    function rpLine(card, texts, lang) {
        var F = deps.R.facts(card);
        if (F.mfrInEU) return '';
        var r = card.rp || {};
        var body = party(r);
        if (!body) return '';
        var role = r.role === 'importer' ? 'importer' : r.role === 'fulfilment_service_provider' ? 'fulfilment_service_provider' : 'authorised_representative';
        return term(texts, lang, role) + ' (EU): ' + body;
    }

    /* ── textile composition, by language ────────────────────────────────── */
    function fibreName(texts, lang, f) {
        var T = texts[lang];
        if (!T || !T.fibres) return null;
        if (+f.n === 2) return T.animalHair && T.animalHair[f.sub | 0] ? T.animalHair[f.sub | 0] : null;
        var raw = T.fibres[f.n];
        return raw ? raw.replace(/\s*\((?:m|f)\.\)/g, '').replace(/\s{2,}/g, ' ').trim() : null;
    }
    function pct(n, lang) {
        var v = Math.round((+n || 0) * 10) / 10;
        var s = String(v);
        if (/^(de|fr|es|it|pt|nl|pl|cs|sk|sl|hr|hu|ro|bg|el|da|sv|fi|et|lv|lt)$/.test(lang)) s = s.replace('.', ',');
        return s + ' %';
    }
    /* Descending by weight (1007/2011 Art. 9(1)); ties keep the card order. */
    function fibreLines(card, texts, langs) {
        var fibres = ((card.textile && card.textile.fibres) || []).filter(function (f) { return f && f.n && +f.pct > 0; })
            .map(function (f, i) { return { f: f, i: i }; })
            .sort(function (a, b) { return (+b.f.pct) - (+a.f.pct) || a.i - b.i; })
            .map(function (x) { return x.f; });
        if (!fibres.length) return [];
        var multi = langs.length > 1;
        var out = [];
        langs.forEach(function (lang) {
            var names = fibres.map(function (f) { var n = fibreName(texts, lang, f); return n ? pct(f.pct, lang) + ' ' + n : null; });
            if (names.some(function (n) { return n == null; })) return; // a language without the name is left out, not guessed
            var line = names.join(', ');
            if (card.textile && card.textile.animal && texts[lang] && texts[lang].animal) line += '. ' + cap(texts[lang].animal);
            out.push((multi ? lang.toUpperCase() + ' ' : '') + line);
        });
        return out;
    }

    /* ── toys: Art. 11(2) word + Annex V Part B, by language ─────────────── */
    function hazardFor(card, lang) {
        var lines = str((card.toy || {}).hazard).split(/\n+/);
        for (var i = 0; i < lines.length; i++) {
            var m = lines[i].match(/^\s*([a-z]{2})\s*[:|]\s*(.+)$/i);
            if (m && m[1].toLowerCase() === lang) return m[2].trim();
        }
        return '';
    }
    function toyLines(card, texts, langs) {
        var t = card.toy || {};
        var points = (t.warnings || []).slice();
        var u3 = t.under3 === 'text3y' ? 1 : (t.under3 === 'text36' ? 0 : null);
        var multi = langs.length > 1;
        var out = [];
        langs.forEach(function (lang) {
            var T = texts[lang];
            if (!T || !T.toy || !T.toyWord) return;
            var said = [];
            var h = hazardFor(card, lang);
            if (u3 != null && T.toy[1] && T.toy[1][u3]) {
                said.push(endDot(T.toy[1][u3]) + (h ? ' ' + endDot(h) : ''));
            } else if (t.under3 === 'pictogram' && h) {
                // The graphic replaces the sentence, not the hazard (Annex V B 1).
                said.push(endDot(h));
            }
            points.forEach(function (p) {
                var w = T.toy[p] && T.toy[p][0];
                if (!w) return;
                if (+p === 4) w = w.replace('{age}', String(t.age || '…'));
                said.push(endDot(w));
            });
            if (!said.length) return;
            var word = said.length > 1 && T.toyWord.warnings ? T.toyWord.warnings : T.toyWord.warning;
            out.push((multi ? lang.toUpperCase() + ' ' : '') + endDot(cap(word)) + ' ' + said.join(' '));
        });
        return out;
    }

    /* ── CLP, by language ────────────────────────────────────────────────── */
    function clpLines(card, texts, langs) {
        var c = card.clp || {};
        var codes = (c.codes || []).slice();
        if (!codes.length) return [];
        var multi = langs.length > 1;
        var subst = str(c.substances);
        var out = [];
        langs.forEach(function (lang) {
            var T = texts[lang];
            if (!T || !T.clp) return;
            var parts = [];
            // The signal word as the act writes it (first letter capital); no
            // upper-casing — Greek capitals drop their accents, others do not.
            if (c.signal && T.signal && T.signal[c.signal]) parts.push(cap(T.signal[c.signal]) + '.');
            codes.forEach(function (code) {
                var kind = /^EUH/.test(code) ? 'EUH' : code.charAt(0);
                var s = T.clp[kind] && T.clp[kind][code];
                if (!s) return;
                if (code === 'EUH208' && subst) s = s.replace(/<[^>]*>/, subst);
                parts.push(s);
            });
            if (parts.length) out.push((multi ? lang.toUpperCase() + ' ' : '') + parts.join(' '));
        });
        return out;
    }

    /* ── cosmetics, by language ──────────────────────────────────────────── */
    function cosmeticLines(card, texts, langs) {
        var c = card.cosmetics || {};
        var out = [];
        if (str(c.content)) out.push(str(c.content) + (str(c.pao) ? '   ' + str(c.pao) + ' M' : ''));
        if (str(c.bbd)) {
            var bb = langs.map(function (l) { var T = texts[l] && texts[l].cosmetics; return T ? noSlot(T.bestBefore) : null; }).filter(Boolean);
            if (bb.length) out.push(bb.join(' / ') + ' ' + str(c.bbd));
        }
        if (str(c.precautions)) out.push(str(c.precautions));
        if (str(c.fn)) out.push(str(c.fn));
        if (str(c.inci)) {
            var ing = (texts.en && texts.en.cosmetics && texts.en.cosmetics.ingredients) || 'ingredients';
            out.push(cap(ing) + ': ' + str(c.inci));
        }
        return out;
    }

    /* ── packaging: France / Italy / Spain ──────────────────────────────── */
    function packParts(card) {
        return ((card.packaging || {}).parts || []).filter(function (p) { return p && p.code && deps.R.PACK_CODES[p.code]; });
    }
    function frLines(card) {
        return packParts(card).map(function (p) { return (str(p.name_fr) || str(p.name) || deps.R.packCode(p.code)) + ' : ' + deps.R.destination('fr', p.code); });
    }
    function itLines(card) {
        var parts = packParts(card);
        if (!parts.length) return [];
        var lines = parts.map(function (p) { return (str(p.name_it) || str(p.name) || '') + ' ' + deps.R.packCode(p.code) + ' — ' + deps.R.destination('it', p.code); });
        lines.push(deps.R.DEST.it.tail + '.');
        return lines.map(function (s) { return s.trim(); });
    }
    function esLines(card) {
        return packParts(card).map(function (p) {
            var colour = deps.R.destination('es', p.code);
            var c = deps.S.ES_CONTAINERS[colour];
            return (str(p.name_es) || str(p.name) || deps.R.packCode(p.code)) + ': ' + (c ? c.word : colour);
        });
    }

    /* ══ blocks for a label kind ════════════════════════════════════════════
     * ctx = { texts: {lang: data}, langs: [...], caption: 'en'|lang,
     *         i18n: { insert_title, contact, ... } (UI words for the insert) }
     */
    function blocks(card, kind, ctx) {
        card = card || {};
        ctx = ctx || {};
        var texts = ctx.texts || {};
        var ev = deps.R.evaluate(card, ctx.today);
        var langs = ctx.langs || ev.languages;
        var capLang = captionLang(texts, ctx.caption || 'en');
        var F = ev.facts;
        var out = [];
        var push = function (b) { if (b && (b.t !== 'text' || str(b.text))) out.push(b); };

        if (kind === 'insert' && ctx.i18n && ctx.i18n.insert_title) push({ t: 'text', text: ctx.i18n.insert_title, role: 'title' });
        if (str(card.name) && kind !== 'product') push({ t: 'text', text: str(card.name), role: 'title' });
        else if (str(card.name) && card.nameOnProduct) push({ t: 'text', text: str(card.name), role: 'title' });
        push({ t: 'text', text: idLine(card), role: 'body', bold: true });
        push({ t: 'text', text: mfrLine(card, texts, capLang), role: 'body' });
        push({ t: 'text', text: rpLine(card, texts, capLang), role: 'body' });

        var syms = [];
        if (kind === 'product') {
            ev.symbols.on.forEach(function (id) { if (id !== 'triman' && id !== 'esbin') syms.push(id); });
            if (ev.facts.isFoodContact && card.fcmSymbol !== false) syms.push('fcm');
        }
        if (kind === 'product' || kind === 'insert') {
            fibreLines(card, texts, langs).forEach(function (s) { push({ t: 'text', text: s, role: 'body' }); });
        }
        if (F.isToy) {
            var tl = toyLines(card, texts, langs);
            if (kind !== 'insert' || tl.length) tl.forEach(function (s) { push({ t: 'text', text: s, role: 'warn', bold: true }); });
            if (kind === 'pack' && card.toy && card.toy.under3 === 'pictogram') syms.push('age03');
        }
        if (F.isClp && kind !== 'insert') {
            clpLines(card, texts, langs).forEach(function (s) { push({ t: 'text', text: s, role: 'body' }); });
            var c = card.clp || {};
            // Пиктограмма опасности стоит рядом со своими фразами на любой этикетке CLP (ст. 17(1)(e)
            // Рег. 1272/2008): на упаковке свечи были фразы H/P без GHS07 (QA 23.10.2026).
            if (kind === 'pack') (c.pictos || []).forEach(function (p) { if (p === 'ghs07' || p === 'ghs09') syms.push(p); });
            if (str(c.qty)) push({ t: 'text', text: str(c.qty), role: 'body' });
            if (str(c.ufi)) push({ t: 'text', text: 'UFI: ' + str(c.ufi), role: 'body', bold: true });
        }
        if (F.isCosmetic && kind !== 'insert') {
            cosmeticLines(card, texts, langs).forEach(function (s) { push({ t: 'text', text: s, role: 'body' }); });
            if (str((card.cosmetics || {}).pao)) syms.push('pao');
            else if (str((card.cosmetics || {}).bbd)) syms.push('hourglass');
        }
        if (F.hasBattery && kind === 'product') {
            var b = card.battery || {};
            var marks = [b.cd ? 'Cd' : '', b.pb ? 'Pb' : ''].filter(Boolean).join(' ');
            if (marks) push({ t: 'text', text: marks, role: 'cdpb', bold: true });
        }
        if (kind === 'pack') {
            /* The Triman is never separated from its sorting instruction
               (ADEME charter): one row, the mark on the left, the lines next
               to it. Spain: each part next to a container in its colour, the
               colour also written out. Italy: codes and words only. */
            if (ev.markets.indexOf('fr') >= 0 && packParts(card).length) {
                out.push({ t: 'symtext', sym: 'triman', text: frLines(card).join('\n'), role: 'small', lang: 'fr' });
            }
            if (F.radio) {
                var u = card.usbc || {};
                var mn = str(u.min), mx = str(u.max);
                var power = mn || mx ? (mn && mx && mn !== mx ? mn + '–' + mx : (mx || mn)) + ' W' : '';
                out.push({ t: 'usbc', included: u.included === 'yes', power: power, pd: !!u.pd });
            }
            if (ev.markets.indexOf('it') >= 0) itLines(card).forEach(function (s) { push({ t: 'text', text: s, role: 'small', lang: 'it' }); });
            if (ev.markets.indexOf('es') >= 0) {
                packParts(card).forEach(function (p, i) {
                    var colour = deps.R.destination('es', p.code);
                    var c = deps.S.ES_CONTAINERS[colour];
                    out.push({ t: 'symtext', sym: 'esbin', opts: { colour: c ? c.colour : null }, text: esLines(card)[i], role: 'small', lang: 'es' });
                });
            }
        }
        if (kind === 'insert') {
            if (str(card.contact)) push({ t: 'text', text: (ctx.i18n && ctx.i18n.contact ? ctx.i18n.contact + ': ' : '') + str(card.contact), role: 'body' });
            if (F.isClp) clpLines(card, texts, langs).forEach(function (s) { push({ t: 'text', text: s, role: 'small' }); });
        }
        if (syms.length) out.push({ t: 'symbols', ids: dedupe(syms), nb: str((card.ce || {}).nb) });
        return out;
    }
    function dedupe(a) { var s = {}; return a.filter(function (x) { if (s[x]) return false; s[x] = 1; return true; }); }

    /* ══ the stack ══════════════════════════════════════════════════════════
     * label = {w, h} mm. opts = { measure, safe (1.5), base (mm em size),
     * xh (1.2 | 0.9 x-height floor for warnings), gap (mm), symH (mm) }.
     * Returns { elements, symbols, size, fits, overflowMm, minSize }. */
    var ROLE = { title: 1.3, body: 1, warn: 1, small: 0.92, cdpb: 1 };
    function stack(list, label, opts) {
        opts = opts || {};
        var L = deps.L, S = deps.S;
        var measure = opts.measure || (L && L.defaultMeasure);
        var safe = opts.safe == null ? 1.5 : opts.safe;
        var W = Math.max(1, label.w - safe * 2), H = Math.max(1, label.h - safe * 2);
        var gap = opts.gap == null ? 0.7 : opts.gap;
        var lh = 1.22;
        var xh = opts.xh || 1.2;
        // The type may shrink to the size whose x-height is the floor, never below
        // label-maker's own floor (2.2 mm em).
        var floor = Math.max(L ? L.MIN_FONT_MM : 2.2, S.minSizeForXHeight(xh, false));
        var base = Math.max(floor, opts.base || 3);
        var hasSym = list.some(function (b) { return b.t === 'symbols'; });

        function symHeight() {
            // Tall enough for the strictest mark in the row (CE 5, Triman 6/10,
            // CLP 1 cm² square), else 8 mm; never more than a third of the label.
            var row = list.filter(function (b) { return b.t === 'symbols'; })[0];
            if (!row) return 0;
            var need = 0;
            row.ids.forEach(function (id) {
                var def = S.SYMBOLS[id];
                if (!def) return;
                if (id === 'ghs07' || id === 'ghs09') need = Math.max(need, S.clpRule(opts.capacityL || 0.5, label.w, label.h).minBox / 0.984);
                else if (id === 'triman') need = Math.max(need, H >= 40 ? 10 : 6);
                else if (def.minH) need = Math.max(need, def.minH);
            });
            return Math.min(Math.max(need, opts.symH || 8), Math.max(need, H / 3));
        }

        function attempt(size) {
            var y = 0, els = [];
            list.forEach(function (b) {
                if (b.t === 'usbc') {
                    // The charger pictogram and the power label side by side,
                    // «a» = 10 mm (law: ≥ 7 mm) so «15–45 W» fits the body at a
                    // legible size; the text sits inside the label's body square.
                    var a = Math.max(7, Math.min(10, (W - 2) / (4 / 3 + 1)));
                    var pw = a * 4 / 3, ph = a * 11 / 6;
                    var raw = { b: b, y: y, rowH: ph, raw: true, syms: [], texts: [] };
                    raw.syms.push({ id: b.included ? 'usbcIn' : 'usbcOut', x: 0, y: y, w: pw, h: ph });
                    if (b.power) {
                        var lx = pw + 2, ly = y + (ph - a * 1.5);
                        raw.syms.push({ id: 'usbcLabel', x: lx, y: ly, w: a, h: a * 1.5 });
                        var ts = Math.max(L ? L.MIN_FONT_MM : 2.2, a * 0.24);
                        raw.texts.push({ text: b.power + (b.pd ? '\nUSB PD' : ''), x: lx + 0.4, y: ly + a / 2 + 0.4, w: a - 0.8, h: a - 0.8, size: ts, align: 'center', valign: 'middle', bold: true });
                    }
                    els.push(raw);
                    y += ph + gap;
                    return;
                }
                if (b.t === 'symtext') {
                    // A mark with its text beside it: the mark is as tall as the
                    // text, never under its own minimum (Triman 6 mm).
                    var def = S.SYMBOLS[b.sym];
                    var st = size * (ROLE[b.role] || 1);
                    st = Math.max(st, L ? L.MIN_FONT_MM : 2.2);
                    var minH = b.sym === 'triman' ? (H >= 40 ? 10 : 6) : Math.max(3.2, st * lh);
                    var guessH = Math.max(minH, st * lh);
                    var sw = guessH * def.vb[0] / def.vb[1];
                    var tw = Math.max(5, W - sw - 1.6);
                    var lines2 = L.wrapText(b.text, tw, { size: st, bold: !!b.bold, font: 'noto', lineHeight: lh }, measure);
                    var th = lines2.length * st * lh;
                    var symH2 = Math.max(minH, Math.min(th, minH * 1.6));
                    var sw2 = symH2 * def.vb[0] / def.vb[1];
                    var rowH = Math.max(symH2, th);
                    els.push({ b: b, y: y, h: th, size: st, lines: lines2, sym: { id: b.sym, x: 0, y: y + (rowH - symH2) / 2, w: sw2, h: symH2, opts: b.opts || null }, textX: sw2 + 1.6, rowH: rowH });
                    y += rowH + gap;
                    return;
                }
                if (b.t !== 'text') return;
                var s = size * (ROLE[b.role] || 1);
                if (b.role === 'warn' || b.role === 'cdpb') s = Math.max(s, floor);
                s = Math.max(s, L ? L.MIN_FONT_MM : 2.2);
                var style = { size: s, bold: !!b.bold, font: 'noto', lineHeight: lh, mode: 'fixed' };
                var lines = L.wrapText(b.text, W, style, measure);
                var h = lines.length * s * lh;
                els.push({ b: b, y: y, h: h, size: s, lines: lines });
                y += h + gap;
            });
            var sh = hasSym ? symHeight() : 0;
            var total = y - (els.length ? gap : 0) + (sh ? (els.length ? gap : 0) + sh : 0);
            return { els: els, total: total, symH: sh };
        }

        var size = base, r = attempt(size);
        while (r.total > H + 1e-6 && size > floor + 1e-6) {
            size = Math.max(floor, size - 0.1);
            r = attempt(size);
        }
        var fits = r.total <= H + 1e-6;

        // label-maker elements, in fractions of the inner box.
        var symbols = [];
        var rawTexts = [];
        r.els.forEach(function (e) {
            if (e.sym) symbols.push({ id: e.sym.id, x: safe + e.sym.x, y: safe + e.sym.y, w: e.sym.w, h: e.sym.h, opts: e.sym.opts });
            if (e.raw) {
                e.syms.forEach(function (q) { symbols.push({ id: q.id, x: safe + q.x, y: safe + q.y, w: q.w, h: q.h }); });
                e.texts.forEach(function (q) {
                    rawTexts.push({ type: 'text', text: q.text, x: q.x / W, y: q.y / H, w: q.w / W, h: q.h / H, size: q.size, mode: 'fixed', align: q.align, valign: q.valign, font: 'noto', bold: !!q.bold, lineHeight: 1.15, color: '#000000', name: 'usbc' });
                });
            }
        });
        var elements = r.els.filter(function (e) { return !e.raw; }).map(function (e) {
            var x0 = e.textX ? e.textX / W : 0;
            var ty = e.rowH ? e.y + (e.rowH - e.h) / 2 : e.y;
            return {
                type: 'text', text: e.b.text, x: x0, y: ty / H, w: 1 - x0, h: Math.min(1, (e.h + 0.05) / H),
                size: e.size, mode: 'fixed', align: 'left', valign: 'top', font: 'noto',
                bold: !!e.b.bold, lineHeight: lh, color: '#000000', name: e.b.role,
            };
        });
        elements = elements.concat(rawTexts);
        // Symbols: one row under the text, left to right, at the computed height.
        if (r.symH) {
            var row = list.filter(function (b) { return b.t === 'symbols'; })[0];
            var x = 0, sy = r.total - r.symH;
            row.ids.forEach(function (id) {
                var def = S.SYMBOLS[id];
                if (!def) return;
                var w = r.symH * def.vb[0] / def.vb[1];
                if (x + w > W + 1e-6) return; // no room on this row: reported below
                symbols.push({ id: id, x: safe + x, y: safe + sy, w: w, h: r.symH });
                x += w + 1.6;
                if (id === 'ce' && row.nb) {
                    // The notified body number follows the CE marking (765/2008 Art. 30, Decision 768/2008 Annex I R12).
                    elements.push({ type: 'text', text: row.nb, x: x / W, y: sy / H, w: Math.min(1, 12 / W), h: r.symH / H, size: Math.max(2.2, r.symH * 0.5), mode: 'fixed', align: 'left', valign: 'middle', font: 'noto', bold: true, lineHeight: 1.1, color: '#000000', name: 'nb' });
                    x += 12;
                }
            });
            var dropped = row.ids.filter(function (id) { return !symbols.some(function (s) { return s.id === id; }); });
            if (dropped.length) { fits = false; r.dropped = dropped; }
        }
        return {
            elements: elements, symbols: symbols, size: size, floor: floor,
            fits: fits, overflowMm: fits ? 0 : Math.max(0, r.total - H),
            dropped: r.dropped || [], symH: r.symH, inner: { w: W, h: H },
        };
    }

    /* ══ marketplace fields and the website's legal block ═══════════════════ */
    function listing(card, ctx) {
        ctx = ctx || {};
        var texts = ctx.texts || {};
        var ev = deps.R.evaluate(card, ctx.today);
        var capLang = captionLang(texts, ctx.caption || 'en');
        var langs = ctx.langs || ev.languages;
        var safety = [];
        if (ev.facts.isToy) safety = safety.concat(toyLines(card, texts, langs));
        if (ev.facts.isClp) safety = safety.concat(clpLines(card, texts, langs));
        if (ev.facts.isTextile) safety = safety.concat(fibreLines(card, texts, langs));
        var epr = card.epr || {};
        var legal = [];
        if (str(epr.fr_idu)) legal.push('FR — Identifiant unique (IDU): ' + str(epr.fr_idu));
        if (str(epr.de_lucid)) legal.push('DE — LUCID: ' + str(epr.de_lucid));
        if (str(epr.de_weee)) legal.push('DE — WEEE-Reg.-Nr.: ' + str(epr.de_weee));
        Object.keys(epr).forEach(function (k) {
            if (/^(fr_idu|de_lucid|de_weee)$/.test(k) || !str(epr[k])) return;
            var m = k.match(/^([a-z]{2})_(.+)$/);
            legal.push((m ? m[1].toUpperCase() + ' — ' + m[2] : k) + ': ' + str(epr[k]));
        });
        return {
            manufacturer: party(card.mfr),
            manufacturerLine: mfrLine(card, texts, capLang),
            rp: ev.facts.mfrInEU ? '' : party(card.rp),
            rpLine: rpLine(card, texts, capLang),
            identification: [str(card.name), idLine(card)].filter(Boolean).join(' — '),
            safety: safety,
            legal: legal,
            languages: langs,
        };
    }

    /* The card's daily-cap unit: everything that reaches paper, hashed. Two
     * exports of the same product the same day are one unit. */
    function cardKey(card) {
        var c = card || {};
        var s = JSON.stringify([c.name, c.category, c.extras, c.type, c.batch, c.serial, c.markets, c.mfr, c.rp, c.textile, c.toy, c.battery, c.cosmetics, c.clp, c.packaging, c.epr, c.ce]);
        var h = 5381;
        for (var i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
        return 'eusl:' + h.toString(36);
    }

    return {
        use: use,
        blocks: blocks, stack: stack, listing: listing, cardKey: cardKey,
        fibreLines: fibreLines, toyLines: toyLines, clpLines: clpLines, cosmeticLines: cosmeticLines,
        idLine: idLine, mfrLine: mfrLine, rpLine: rpLine, frLines: frLines, itLines: itLines, esLines: esLines,
        captionLang: captionLang,
    };
});
